import React from 'react';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';

/**
 * Monthly Preparedness Calendar
 * 
 * 12-month action plan with specific tasks for each month
 * to build and maintain emergency readiness
 */

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 9,
    backgroundColor: '#ffffff',
  },
  title: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: '#1a1a1a',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 20,
    textAlign: 'center',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  monthCard: {
    width: '32%',
    marginRight: '2%',
    marginBottom: 10,
    border: '1pt solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
  },
  monthHeader: {
    padding: 6,
    backgroundColor: '#1f2937',
  },
  monthName: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  focusArea: {
    fontSize: 7,
    color: '#f97316',
    textAlign: 'center',
    marginTop: 2,
  },
  monthBody: {
    padding: 8,
    backgroundColor: '#f9fafb',
    minHeight: 80,
  },
  taskItem: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'flex-start',
  },
  checkbox: {
    width: 8,
    height: 8,
    border: '1pt solid #6b7280',
    marginRight: 4,
    marginTop: 1,
    borderRadius: 2,
  },
  taskText: {
    fontSize: 7,
    color: '#374151',
    flex: 1,
    lineHeight: 1.3,
  },
  legend: {
    marginTop: 15,
    padding: 12,
    backgroundColor: '#f0fdf4',
    borderRadius: 4,
    border: '1pt solid #86efac',
  },
  legendTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#166534',
    marginBottom: 8,
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  legendItem: {
    width: '33%',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  legendText: {
    fontSize: 7,
    color: '#374151',
  },
  footer: {
    position: 'absolute',
    bottom: 25,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 8,
    color: '#9ca3af',
  },
});

// Default monthly tasks if none provided
const defaultMonthlyTasks = [
  { month: 'January', focus: 'Planning', tasks: ['Review and update emergency plan', 'Check smoke detector batteries', 'Inventory emergency supplies'] },
  { month: 'February', focus: 'Communication', tasks: ['Update emergency contact list', 'Practice family communication plan', 'Test emergency alert apps'] },
  { month: 'March', focus: 'Water Safety', tasks: ['Check water heater for leaks', 'Stock/rotate water supplies', 'Review flood evacuation routes'] },
  { month: 'April', focus: 'Severe Weather', tasks: ['Check weather radio batteries', 'Review tornado/storm shelter plan', 'Practice take-cover drills'] },
  { month: 'May', focus: 'Fire Safety', tasks: ['Clear brush and defensible space', 'Check fire extinguisher expiration', 'Review wildfire evacuation plan'] },
  { month: 'June', focus: 'Summer Prep', tasks: ['Update go-bag with summer items', 'Check A/C and ventilation', 'Review heat emergency protocol'] },
  { month: 'July', focus: 'Documents', tasks: ['Backup important documents', 'Update insurance policies', 'Review digital security'] },
  { month: 'August', focus: 'School Ready', tasks: ['Update school emergency contacts', 'Review pickup procedures', 'Check student supply kits'] },
  { month: 'September', focus: 'National Prep', tasks: ['Participate in National Preparedness Month', 'Take a CPR/First Aid class', 'Practice family drills'] },
  { month: 'October', focus: 'Fall Check', tasks: ['Check heating systems', 'Rotate food supplies', 'Update cold weather gear'] },
  { month: 'November', focus: 'Medical', tasks: ['Refill prescription supplies', 'Update medical information cards', 'Check first aid kit supplies'] },
  { month: 'December', focus: 'Year-End', tasks: ['Complete annual plan review', 'Set next year goals', 'Test all emergency equipment'] },
];

interface MonthlyCalendarProps {
  calendar?: {
    month: string;
    focus_area: string;
    tasks: string[];
  }[];
  year?: number;
}

export const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({ 
  calendar = defaultMonthlyTasks.map(m => ({ month: m.month, focus_area: m.focus, tasks: m.tasks })),
  year = new Date().getFullYear() 
}) => {
  // Ensure we have 12 months
  const months = calendar.length === 12 ? calendar : defaultMonthlyTasks.map(m => ({
    month: m.month,
    focus_area: m.focus,
    tasks: m.tasks,
  }));

  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>📅 Monthly Preparedness Calendar</Text>
      <Text style={styles.subtitle}>
        {year} Action Plan — Check off tasks as you complete them
      </Text>

      {/* First row: Jan-Mar */}
      <View style={styles.row}>
        {months.slice(0, 3).map((month, idx) => (
          <View key={idx} style={styles.monthCard}>
            <View style={styles.monthHeader}>
              <Text style={styles.monthName}>{month.month}</Text>
              <Text style={styles.focusArea}>{month.focus_area}</Text>
            </View>
            <View style={styles.monthBody}>
              {month.tasks.slice(0, 4).map((task, tidx) => (
                <View key={tidx} style={styles.taskItem}>
                  <View style={styles.checkbox} />
                  <Text style={styles.taskText}>{task}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>

      {/* Second row: Apr-Jun */}
      <View style={styles.row}>
        {months.slice(3, 6).map((month, idx) => (
          <View key={idx} style={styles.monthCard}>
            <View style={{ ...styles.monthHeader, backgroundColor: '#166534' }}>
              <Text style={styles.monthName}>{month.month}</Text>
              <Text style={styles.focusArea}>{month.focus_area}</Text>
            </View>
            <View style={styles.monthBody}>
              {month.tasks.slice(0, 4).map((task, tidx) => (
                <View key={tidx} style={styles.taskItem}>
                  <View style={styles.checkbox} />
                  <Text style={styles.taskText}>{task}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>

      {/* Third row: Jul-Sep */}
      <View style={styles.row}>
        {months.slice(6, 9).map((month, idx) => (
          <View key={idx} style={styles.monthCard}>
            <View style={{ ...styles.monthHeader, backgroundColor: '#b45309' }}>
              <Text style={styles.monthName}>{month.month}</Text>
              <Text style={styles.focusArea}>{month.focus_area}</Text>
            </View>
            <View style={styles.monthBody}>
              {month.tasks.slice(0, 4).map((task, tidx) => (
                <View key={tidx} style={styles.taskItem}>
                  <View style={styles.checkbox} />
                  <Text style={styles.taskText}>{task}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>

      {/* Fourth row: Oct-Dec */}
      <View style={styles.row}>
        {months.slice(9, 12).map((month, idx) => (
          <View key={idx} style={styles.monthCard}>
            <View style={{ ...styles.monthHeader, backgroundColor: '#0e7490' }}>
              <Text style={styles.monthName}>{month.month}</Text>
              <Text style={styles.focusArea}>{month.focus_area}</Text>
            </View>
            <View style={styles.monthBody}>
              {month.tasks.slice(0, 4).map((task, tidx) => (
                <View key={tidx} style={styles.taskItem}>
                  <View style={styles.checkbox} />
                  <Text style={styles.taskText}>{task}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        <Text style={styles.legendTitle}>Focus Areas by Season</Text>
        <View style={styles.legendRow}>
          <View style={styles.legendItem}>
            <View style={{ ...styles.legendDot, backgroundColor: '#1f2937' }} />
            <Text style={styles.legendText}>Winter (Jan-Mar): Planning & Communication</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={{ ...styles.legendDot, backgroundColor: '#166534' }} />
            <Text style={styles.legendText}>Spring (Apr-Jun): Weather & Fire Safety</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={{ ...styles.legendDot, backgroundColor: '#b45309' }} />
            <Text style={styles.legendText}>Summer (Jul-Sep): Documents & Training</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={{ ...styles.legendDot, backgroundColor: '#0e7490' }} />
            <Text style={styles.legendText}>Fall (Oct-Dec): Review & Maintenance</Text>
          </View>
        </View>
      </View>

      <Text style={styles.footer}>
        Monthly Preparedness Calendar | Post in a visible location and check off completed tasks
      </Text>
    </Page>
  );
};

export default MonthlyCalendar;
